import { useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { attacking } from "../../atoms/attacking";
import { match } from "../../atoms/match";
import { IAttackingTeam } from "../../interfaces/attacking.interface";

/* ------------------------------------------------------------------ */
/*  Metric explanation map                                             */
/* ------------------------------------------------------------------ */

const metricDescriptions: Record<string, string> = {
  "Shot accuracy":
    "Shots on target / Total shots",
  "Shot conversion rate":
    "Goals / Total shots",
  "On target conversion":
    "Goals / Shots on target",
  "Possession effectiveness":
    "Possessions with shot % / Ball possession %",
  "Shots per possession":
    "Total shots / Total possessions",
  "Team intensity index":
    "Combined measure of a team's pressing and transition intensity during the match.",
  "Control index":
    "Reflects how well a team maintains territorial and possession control.",
  "Chaos index":
    "Measures the unpredictability and disorder in a team's play patterns.",
  "Pass intensity":
    "Total passes / Ball possession %. Indicates how actively a team uses the ball while in possession.",
  "Tempo index":
    "Passes per possession.",
  "Directness index":
    "Shots / Passes. Measures how directly a team converts passing into shooting opportunities.",
};

/* ------------------------------------------------------------------ */
/*  Info-icon tooltip component                                        */
/* ------------------------------------------------------------------ */

function InfoTooltip({ text }: { text: string }) {
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setVisible(true);
  };

  const hide = () => {
    hideTimer.current = setTimeout(() => setVisible(false), 150);
  };

  const toggle = () => setVisible((v) => !v);

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onClick={toggle}
        onMouseEnter={show}
        onMouseLeave={hide}
        className="flex items-center justify-center w-[16px] h-[16px] rounded-full
                   bg-[#6886BD] dark:bg-[#3B5998] text-white text-[10px] leading-none
                   cursor-pointer select-none hover:bg-[#4f6da3] dark:hover:bg-[#2d4680]
                   transition-colors"
        aria-label="Info"
      >
        i
      </button>

      {visible && (
        <span
          onMouseEnter={show}
          onMouseLeave={hide}
          className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-[6px]
                     w-[220px] px-[10px] py-[8px] rounded-[6px]
                     bg-[#2c3e5a] dark:bg-[#1a2744] text-white text-[12px] leading-[1.4]
                     text-left shadow-lg pointer-events-auto
                     before:content-[''] before:absolute before:top-full before:left-1/2
                     before:-translate-x-1/2 before:border-[5px] before:border-transparent
                     before:border-t-[#2c3e5a] dark:before:border-t-[#1a2744]"
        >
          {text}
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

interface StatRow {
  key: string;
  home: string;
  away: string;
}

function safeRatio(v: unknown): string {
  if (typeof v === "number" && !isNaN(v)) return v.toFixed(2);
  return "-";
}

function buildMetricRows(home: IAttackingTeam, away: IAttackingTeam): StatRow[] {
  const metricKeys: { key: string; metric: keyof IAttackingTeam["metrics"] }[] = [
    { key: "Shot accuracy", metric: "shot_accuracy" },
    { key: "Shot conversion rate", metric: "shot_conversion_rate" },
    { key: "On target conversion", metric: "on_target_conversion" },
    { key: "Possession effectiveness", metric: "possession_effectiveness" },
    { key: "Shots per possession", metric: "shots_per_possession" },
    { key: "Team intensity index", metric: "team_intensity_index" },
    { key: "Control index", metric: "control_index" },
    { key: "Chaos index", metric: "chaos_index" },
    { key: "Pass intensity", metric: "pass_intensity" },
    { key: "Tempo index", metric: "tempo_index" },
    { key: "Directness index", metric: "directness_index" },
  ];

  return metricKeys.map(({ key, metric }) => ({
    key,
    home: safeRatio(home.metrics?.[metric]?.ratio),
    away: safeRatio(away.metrics?.[metric]?.ratio),
  }));
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function AttackingSection() {
  const data = useRecoilValue(attacking);
  const matchData = useRecoilValue(match);
  const { t } = useTranslation();

  if (!data?.home || !data?.away) return null;

  const { home, away } = data;
  const allRows = buildMetricRows(home, away);

  return (
    <div className="flex-1 rounded-[10px] shadow-teams">
      {/* Section header: Additional Parameters */}
      <div className="bg-[#6886BD] dark:bg-[#3B5998] py-[7px] px-[24px] rounded-t-[10px] text-center text-white text-[20px]">
        {t("Additional parameters").toLocaleUpperCase()}
      </div>

      {/* Sub-section header: Attacking */}
      <div className="bg-[#6886BD99] dark:bg-[#3B599899] py-[5px] px-[24px] text-center text-white text-[16px] font-medium">
        {t("Attacking").toLocaleUpperCase()}
      </div>

      {/* Team names sub-header */}
      <div className="flex justify-around bg-[#6886BD44] dark:bg-[#3B599844] dark:text-white py-[7px]">
        <div className="flex items-center gap-[10px]">
          <img
            className="w-[30px] h-[35px]"
            src={`${matchData.home_team.logo_base || ""}${matchData.home_team.logo || ""}`}
            alt=""
          />
          <p>{home.team_name || ""}</p>
        </div>
        <div className="flex items-center gap-[10px]">
          <img
            className="w-[30px] h-[35px]"
            src={`${matchData.away_team.logo_base || ""}${matchData.away_team.logo || ""}`}
            alt=""
          />
          <p>{away.team_name || ""}</p>
        </div>
      </div>

      {/* Stats table */}
      <table className="w-full text-center dark:text-white dark:bg-[#1B2436]">
        <tbody>
          {allRows.map((row) => (
            <tr
              key={row.key}
              className="border-b border-[#6886BD] dark:border-[#3B5998]"
            >
              <td className="w-[100px] py-[4px]">{row.home}</td>
              <td className="py-[4px] relative">
                {t(row.key)}
                {metricDescriptions[row.key] && (
                  <span className="absolute right-[8px] top-1/2 -translate-y-1/2">
                    <InfoTooltip text={metricDescriptions[row.key]} />
                  </span>
                )}
              </td>
              <td className="w-[100px] py-[4px]">{row.away}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
